import "./login.scss";
import type * as React from "react";
import { type FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router";
import { Link } from "react-router-dom";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type LocationWithState } from "../../_Bootstrap/RoutingAndStore";
import { updateUser } from "../../Actions/UserActions";
import BoxForm from "../../Common/BoxForm/BoxForm";
import Button from "../../Common/Button";
import Input from "../../Common/Input/Input";
import PasswordInput from "../../Common/Input/PasswordInput";
import { type RootState } from "../../Reducers/DeveloperReducers";
import { useTitle } from "../../Utilities/TitleHook";

const Login: FC = () => {
    useTitle("Login");

    const [ username, setUsername ] = useState("");
    const [ password, setPassword ] = useState("");
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<null | string>(null);
    const [ resetRequested, setResetRequested ] = useState<null | "REQUESTED" | "SENT">(null);
    const [ resetEmail, setResetEmail ] = useState("");
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user.user);
    const location = useLocation() as LocationWithState<{
        from: string;
    }>;

    if(typeof user !== "boolean") {
        let to = "/applications";

        if(location.state?.from) {
            to = location.state.from;
        }

        return <Navigate to={to} />;
    }

    let loginText: React.ReactNode = "Sign In";

    if(loading) {
        loginText = <FontAwesomeLight icon="spinner-third" spin />;
    }

    const onSubmit = (): void => {
        // Check that we aren't already loading
        if(loading) {
            return;
        }

        // Check that we have a username and password
        if(!username) {
            setError("Invalid Username");

            return;
        }

        if(!password) {
            setError("Invalid Password");

            return;
        }

        setLoading(true);

        fetch("login", {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                username: username,
                password: password,
            }),
        })
            .then(response => {
                setLoading(false);

                if(response.status !== 200) {
                    throw new Error("Invalid Username / Password Combination");
                }

                return response.json();
            })
            .then(result => {
                dispatch(updateUser(result));
            })
            .catch((err) => {
                console.error(err);

                if(err instanceof Error) {
                    setError(err.message);
                } else {
                    setError(err.toString());
                }
            });
    };

    const onResetRequest = () => {
        if(loading) {
            return;
        }

        if(!resetEmail) {
            setError("Invalid Email");

            return;
        }

        setLoading(true);

        fetch("/auth/forgot", {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                email: resetEmail,
            }),
        })
            .then(response => {
                setLoading(false);

                if(response.status !== 204) {
                    throw new Error("We were unable to process the reset password request");
                }
            })
            .then(() => {
                setResetRequested("SENT");
            })
            .catch((err) => {
                console.error(err);

                if(err instanceof Error) {
                    setError(err.message);
                } else {
                    setError(err.toString());
                }
            });
    };

    if(resetRequested) {
        return (
            <main id="login-box" className="flex-center">
                <BoxForm
                    error={error}
                    onClear={() => {
                        setError(null);
                    }}
                >
                    {resetRequested === "REQUESTED" && (
                        <div>
                            <h2 className="mt-3.5">Request Password Reset</h2>
                            <Input
                                icon="user"
                                className="mt-2 mb-4"
                                label="Email"
                                type="text"
                                showLabel={false}
                                onChange={e => {
                                    setError(null);
                                    setResetEmail(e.currentTarget.value);
                                }}
                                value={resetEmail}
                                onKeyDown={e => {
                                    if(e.key === "Enter") {
                                        onResetRequest();
                                    }
                                }}
                            />

                            <Button onClick={onResetRequest}>Reset Password</Button>

                            <p>
                                <Link to="/register">
                                    Don&#39;t have an account? <span className="link-primary">Register!</span>
                                </Link>
                                <br />
                                <Link to="/login" onClick={() => setResetRequested(null)}>
                                    Or click here to <span className="link-primary">Sign In</span>
                                </Link>
                            </p>
                        </div>
                    )}
                    {resetRequested === "SENT" && (
                        <div>
                            <p>
                                If an account associated with your email address exists,
                                a password reset link will be sent to you shortly.
                            </p>
                        </div>
                    )}
                </BoxForm>
            </main>
        );
    }

    return (
        <main id="login-box" className="flex-center">
            <BoxForm
                error={error}
                onClear={() => {
                    setError(null);
                }}
            >
                <h1>Sign In</h1>
                <Input
                    icon="user"
                    label="Username"
                    type="text"
                    showLabel={false}
                    onChange={e => {
                        setError(null);
                        setUsername(e.currentTarget.value);
                    }}
                    value={username}
                    onKeyDown={e => {
                        if(e.key === "Enter") {
                            onSubmit();
                        }
                    }}
                />
                <PasswordInput
                    icon="key"
                    label="Password"
                    onChange={e => {
                        setError(null);
                        setPassword(e.currentTarget.value);
                    }}
                    value={password}
                    onKeyDown={e => {
                        if(e.key === "Enter") {
                            onSubmit();
                        }
                    }}
                />

                <Button onClick={onSubmit}>{loginText}</Button>

                <p>
                    <Link to="/register">
                        Don&#39;t have an account? <span className="link-primary">Register!</span>
                    </Link>
                    <br />
                    <Link to="#" onClick={() => setResetRequested("REQUESTED")}>
                        <span className="link-primary">Forgot Password?</span>
                    </Link>
                </p>
            </BoxForm>
        </main>
    );
};

export default Login;
